import { useTranslation } from "react-i18next";

import {
  DataTable,
  NARROW_ONLY,
  NARROW_WRAP,
  type NarrowColumn,
  SortableHeadCell,
  SortButton,
  TABLE_ICON_CLASS,
  TableCell,
  TableEmptyRow,
  TableFacts,
  TableHead,
  TableItem,
  TableRow,
} from "@/components/data-table";
import { Highlight } from "@/components/highlight";
import { activityActionLabel, activityDetail } from "@/lib/activity-actions";
import type { AdminActivityLogEntry } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { SortColumns, TableSortState } from "@/lib/table-sort";
import { cn } from "@/lib/utils";

import { ActionIcon } from "./action-icon";

/** The API's sort keys. */
export type ActivityColumn = "action" | "email" | "created_at";

/** Each column's first order: words from A, the newest first. */
export const ACTIVITY_SORT_COLUMNS: SortColumns<ActivityColumn> = {
  action: "asc",
  email: "asc",
  created_at: "desc",
};

interface Props {
  entries: AdminActivityLogEntry[];
  searchTerm: string;
  /** The server's sort (`useTableSort`), which the headers show and change. */
  sort: TableSortState<ActivityColumn>;
  loading?: boolean;
  /** Clears the filters; offered when they leave no entry. */
  onReset?: () => void;
}

/** A column: the API's sort key, the header's text key, its side, and what it does in the phone layout. */
interface Column {
  key: ActivityColumn;
  label: "activity.action" | "analytics.user" | "activity.when";
  align: "left" | "right";
  narrow: NarrowColumn;
}

/** The person's column, which leaves in the phone layout (the person moves under the action). */
const PERSON: Column = { key: "email", label: "analytics.user", align: "left", narrow: "hide" };

const COLUMNS: readonly Column[] = [
  { key: "action", label: "activity.action", align: "left", narrow: "keep" },
  PERSON,
  { key: "created_at", label: "activity.when", align: "right", narrow: "keep" },
];

/**
 * The activity log's entries in the Tools page's style: what happened (an
 * icon, the action in the dashboard's language and the stored detail under
 * it, which is never translated but for a view's role), who did it (name,
 * email under it) and when.
 * Every column sorts; the sorted one says so to screen readers. On a phone the
 * person moves under the action, with their sort button next to the action's,
 * and the time may go under the date.
 */
export function ActivityTable({ entries, searchTerm, sort, loading = false, onReset }: Props) {
  const { t } = useTranslation();
  return (
    <DataTable label={t("nav.activity")} busy={loading}>
      <TableHead>
        {COLUMNS.map((col) => (
          <SortableHeadCell
            key={col.key}
            {...sort.head(col.key)}
            align={col.align}
            narrow={col.narrow}
            // The person's column leaves in the phone layout; its sort stays, here.
            after={
              col.key === "action" && (
                <span className={cn("ml-4", NARROW_ONLY)}>
                  <SortButton {...sort.head(PERSON.key)} sayOrder>
                    {t(PERSON.label)}
                  </SortButton>
                </span>
              )
            }
          >
            {t(col.label)}
          </SortableHeadCell>
        ))}
      </TableHead>
      <tbody>
        {entries.map((entry) => {
          const name = `${entry.first_name ?? ""} ${entry.last_name ?? ""}`.trim();
          const who = entry.email ?? entry.user_id;
          const detail = activityDetail(entry.action, entry.detail);
          return (
            <TableRow key={entry.id}>
              <TableCell>
                <TableItem
                  icon={<ActionIcon action={entry.action} className={TABLE_ICON_CLASS} />}
                  title={<Highlight text={activityActionLabel(entry.action)} query={searchTerm} />}
                  summary={detail && <Highlight text={detail} query={searchTerm} />}
                  facts={
                    <TableFacts>
                      <Highlight text={name || who} query={searchTerm} />
                    </TableFacts>
                  }
                />
              </TableCell>
              <TableCell narrow="hide">
                <p className="text-foreground">
                  <Highlight text={name || who} query={searchTerm} />
                </p>
                {name && (
                  // An email whole on its line, so the column takes its width; it breaks only when it must.
                  <p className="text-muted text-xs break-words">
                    <Highlight text={who} query={searchTerm} />
                  </p>
                )}
              </TableCell>
              <TableCell
                align="right"
                className={cn("text-muted whitespace-nowrap tabular-nums", NARROW_WRAP)}
              >
                {formatDateTime(entry.created_at)}
              </TableCell>
            </TableRow>
          );
        })}
        {entries.length === 0 && (
          <TableEmptyRow colSpan={COLUMNS.length} onReset={loading ? undefined : onReset}>
            {loading ? t("common.loadingEllipsis") : t("activity.empty")}
          </TableEmptyRow>
        )}
      </tbody>
    </DataTable>
  );
}
